import { graphql, Link } from 'gatsby';
import { FixedObject } from 'gatsby-image';
import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/website/Header/Header';
import { css } from '@emotion/react';
import { Category } from '../../components/blog/Category';
import SiteNav from '../../components/blog/header/SiteNav';
import Pagination from '../../components/blog/Pagination';
import { PostCard } from '../../components/blog/PostCard';
import { Wrapper } from '../../components/blog/Wrapper';
import BlogLayout from '../../layouts/BlogLayout';
import {
  inner,
  outer,
  PostFeed,
  Posts,
  SiteDescription,
  SiteHeader,
  SiteHeaderContent,
  SiteMain,
  SiteTitle,
  SiteHeaderStyles,
} from '../../styles/shared';
import config from '../../website-config';
import { PageContext } from './post';
import '../../styles/mdb.min.css';
import '../../styles/global.css';
export interface IndexProps {
  pageContext: {
    currentPage: number;
    numPages: number;
  };
  data: {
    logo: {
      childImageSharp: {
        fixed: FixedObject;
      };
    };
    header: {
      childImageSharp: {
        fixed: FixedObject;
      };
    };
    allMdx: {
      edges: Array<{
        node: PageContext;
      }>;
    };
  };
}

const IndexPage: React.FC<IndexProps> = props => {
  const { width, height } = props.data.header.childImageSharp.fixed;
  const url = typeof window !== 'undefined' ? window.location.href : '';
  return (
    <BlogLayout css={HomePosts}>
      <Helmet>
        <html lang={config.lang} />
        <title>Blog | {config.title}</title>
        <meta name="keywords" content={`${config.keyword}`} />
        <meta name="description" content={config.description} />
        <meta property="og:site_name" content={config.title} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Blog | ${config.title}`} />
        <meta property="og:description" content={`Wordpress and Web Development Blog | ${config.description}` } />
        <meta property="og:url" content={config.blogUrl} />
        <meta
          property="og:image"
          content={`${config.siteUrl}${props.data.header.childImageSharp.fixed.src}`}
        />
        {config.facebook && <meta property="article:publisher" content={config.facebook} />}
        {config.googleSiteVerification && (
          <meta name="google-site-verification" content={config.googleSiteVerification} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.title} />
        <meta name="twitter:description" content={config.description} />
        <meta name="twitter:url" content={config.siteUrl} />
        <meta
          name="twitter:image"
          content={`${config.siteUrl}${props.data.header.childImageSharp.fixed.src}`}
        />
        {config.twitter && (
          <meta
            name="twitter:site"
            content={`@${config.twitter.split('https://twitter.com/')[1]}`}
          />
        )}
        <meta property="og:image:width" content={width.toString()} />
        <meta property="og:image:height" content={height.toString()} />

        <script type="application/ld+json">
        {`
         {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://bizdamn.tech"
            },{
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://bizdamn.tech/blog"
            }]
          },
          {
            "@context": "https://schema.org/",
            "@type": "WebPage",
            "name": "Blog:Business Development & Wordpress and Web Development",
            "speakable":
            {
             "@type": "SpeakableSpecification",
             "xPath": [
               "/html/head/title",
               "/html/head/meta[@name='description']/@content"
               ]
             },
            "url": ${url}
            }
          `}
            </script>
      </Helmet>
      <Wrapper>
        <Header />

        <div 
          css={[outer, SiteHeader, SiteHeaderStyles]}
          className="site-header-background"
          style={{
            backgroundColor: '#1D2B35'
          }}
        >
          <SiteNav isHome />
          <SiteHeaderContent  className="site-header-content">
            <SiteTitle style={{ textAlign: 'center', fontSize: '4rem' }} className="site-title">
              Next-level Strategies to Grow your Business Online
            </SiteTitle>
            <SiteDescription style={{ textAlign: 'center' }}></SiteDescription>
          </SiteHeaderContent>
        </div>

        {/* Main Body Start */}
        <main id="site-main" css={[SiteMain, outer]}>
          <div css={[Posts]}>
            <div css={[PostFeed]}>
              {props.data.allMdx.edges.map((post, index) => {
                // filter out drafts in production
                return (
                  (post.node.frontmatter.draft !== true ||
                    process.env.NODE_ENV !== 'production') && (
                    <PostCard key={post.node.fields.slug} post={post.node} large={index === 0} />
                  )
                );
              })}
            </div>
          </div>
        </main>

        {props.children}
        {props.pageContext.numPages > 1 && (
          <Pagination
            currentPage={props.pageContext.currentPage}
            numPages={props.pageContext.numPages}
          />
        )}

        {/* Categories */}
        <div className="sidebar row">
          <div className="col-lg-12">
            <h3 className="sidebar-title">Categories</h3>
            <div className="sidebar-item categories row">
              <div className="col  categories">
                <ul>
                  {props.data.allMdx.edges.map((post, index) => {
                    // filter out drafts in production
                    return (
                      (post.node.frontmatter.draft !== true ||
                        process.env.NODE_ENV !== 'production') && (
                        <Category
                          key={post.node.fields.slug}
                          post={post.node}
                          large={index === 0}
                        />
                      )
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>


      </Wrapper>
    </BlogLayout>
  );
};

export const pageQuery = graphql`
  query blogPageQuery($skip: Int!, $limit: Int!) {
    logo: file(relativePath: { eq: "avatars/Logo.png" }) {
      childImageSharp {
        # Specify the image processing specifications right in the query.
        # Makes it trivial to update as your page's design changes.
        fixed {
          ...GatsbyImageSharpFixed
        }
      }
    }
    header: file(relativePath: { eq: "img/blog-cover.png" }) {
      childImageSharp {
        # Specify the image processing specifications right in the query.
        # Makes it trivial to update as your page's design changes.
        fixed(width: 2000, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    allMdx(
      sort: { fields: [frontmatter___datemodified], order: DESC }
      filter: {
        frontmatter: { draft: { ne: true } }
        fileAbsolutePath: { regex: "/(blog)/.*.mdx$/" }
      }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          frontmatter {
            title
            date
            datemodified
            tags
            draft
            excerpt
            image {
              childImageSharp {
                fluid(maxWidth: 3720) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            author {
              id
              bio
              avatar {
                children {
                  ... on ImageSharp {
                    fluid(quality: 100, srcSetBreakpoints: [40, 80, 120]) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
          }
          excerpt
          fields {
            readingTime {
              text
            }
            layout
            slug
          }
        }
      }
    }
  }
`;

const HomePosts = css`
  @media (min-width: 795px) {
    .post-card-large {
      flex: 1 1 100%;
      flex-direction: row;
      padding-bottom: 40px;
      min-height: 280px;
      border-top: 0;
    }

    .post-card-large .post-card-title {
      margin-top: 0;
      font-size: 3.2rem;
    }

    .post-card-large:not(.no-image) .post-card-header {
      margin-top: 0;
    }

    .post-card-large .post-card-image-link {
      position: relative;
      flex: 1 1 auto;
      margin-bottom: 0;
      min-height: 380px;
    }

    .post-card-large .post-card-image {
      position: absolute;
      width: 100%;
      height: 100%;
    }

    .post-card-large .post-card-content {
      flex: 0 1 361px;
      justify-content: center;
    }

    .post-card-large .post-card-title {
      margin-top: 0;
      font-size: 3.2rem;
    }

    .post-card-large .post-card-content-link {
      padding: 0 0 0 40px;
    }

    .post-card-large .post-card-meta {
      padding: 0 0 0 40px;
    }

    .post-card-large .post-card-excerpt p {
      margin-bottom: 1.5em;
      font-size: 1.8rem;
      line-height: 1.5em;
    }
  }
`;

export default IndexPage;
